import { type FC, type InputHTMLAttributes, type MouseEventHandler, forwardRef } from "react";

import * as stylex from "@stylexjs/stylex";

import { type OmitStyles, colors, genericStyles } from "../../styles/tokens.stylex";
import ErrorMessage from "../error-message";

export type TextInputProps = OmitStyles<InputHTMLAttributes<HTMLInputElement>> & {
    hasError?: boolean;
    variant: keyof typeof variants;
    label?: string;
    Before?: FC<React.SVGProps<SVGSVGElement>>;
    After?: FC<OmitStyles<React.ButtonHTMLAttributes<HTMLButtonElement>>>;
    errorMessage?: string;
    afterOnClick?: MouseEventHandler<HTMLButtonElement>;
};

const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
    ({ styleX, variant, label, Before, After, errorMessage, id, afterOnClick, ...all }, ref) => {
        return (
            <div {...stylex.props(styleX)}>
                {label && (
                    <label {...stylex.props(styles.label)} htmlFor={id}>
                        {label}
                    </label>
                )}
                <div
                    {...stylex.props(
                        styles.inputContainer,
                        errorMessage ? styles.error : null,
                        variants[variant],
                    )}
                    onClick={(e) =>
                        (
                            (e.currentTarget.children[1] ?? e.currentTarget.children[0]) as HTMLInputElement
                        ).focus()
                    }
                >
                    {Before && <Before {...stylex.props(styles.icon)} />}
                    <input {...all} {...stylex.props(styles.input)} ref={ref} />
                    {After && <After styleX={styles.icon} onClick={afterOnClick} />}
                </div>
                <ErrorMessage errorMessage={errorMessage} />
            </div>
        );
    },
);

const styles = stylex.create({
    label: {
        color: colors.fg_Primary,
        fontSize: "0.875rem",
        lineHeight: "1.43em",
    },
    inputContainer: {
        borderRadius: genericStyles.borderRadiusFullRound,
        transition: "outline-color 150ms ease-in",
        alignItems: "center",
        backgroundColor: colors.bg_Primary,
        display: "flex",
        outlineColor: {
            default: colors.b_Primary,
            ":focus-within": colors.b_Accent,
            ":hover": colors.b_Accent,
        },
        outlineStyle: "solid",
        outlineWidth: "1px",
    },
    error: {
        outlineColor: colors.b_Error,
    },
    input: {
        borderStyle: "none",
        outline: "none",
        color: colors.fg_Secondary,
        flexGrow: 1,
        fontSize: "inherit",
        lineHeight: "1.43em",
    },
    icon: {
        height: "1em",
        width: "1em",
    },
});

const variants = stylex.create({
    sm: {
        padding: ".5rem",
        gap: ".5rem",
        fontSize: "0.875rem",
    },
    md: {
        gap: ".75rem",
        paddingBlock: ".5rem",
        paddingInline: ".75rem",
        fontSize: "1rem",
    },
    lg: {
        padding: ".75rem",
        gap: ".75rem",
        fontSize: "1rem",
    },
});

export default TextInput;
